import { defineVariants } from "@/runtime/style-engine/define-variants";

export const inputClasses = defineVariants({
  base: `
    inline-flex! flex-row! items-center! gap-2!
    min-w-0
    cursor-text
    outline-none
    transition-colors
  `,
  icon: `flex-shrink-0 text-inherit`,
  input: `
    flex-1
    w-full
    min-w-0
    bg-transparent
    border-none
    outline-none
    text-inherit
    font-inherit
    cursor-inherit
    [:where([data-square="true"])_&]:text-center
    [:where([data-disabled="true"])_&]:cursor-not-allowed

    /* Hide native number spinners */
    [&[type="number"]]:[-webkit-appearance:textfield]
    [&[type="number"]]:[-moz-appearance:textfield]
    [&[type="number"]::-webkit-outer-spin-button]:[-webkit-appearance:none]
    [&[type="number"]::-webkit-outer-spin-button]:m-0
    [&[type="number"]::-webkit-inner-spin-button]:[-webkit-appearance:none]
    [&[type="number"]::-webkit-inner-spin-button]:m-0
  `,
  placeholder: `text-(--color-foreground)/50`,

  variants: {
    variant: {
      solid: `hover:bg-(--card-color)/75`,
      outline: `hover:bg-(--card-color)/5`,
      soft: `hover:bg-(--card-color)/15`,
      subtle: `hover:bg-(--card-color)/25`,
      ghost: `hover:bg-(--card-color)/5`,
    },

    size: {
      xs: `px-2! h-6! text-xs rounded!`,
      sm: `px-2.5! h-8! text-sm rounded-md!`,
      md: `px-3! h-10! text-base rounded-lg!`,
      lg: `px-4! h-12! text-lg rounded-lg!`,
      xl: `px-5! h-14! text-xl rounded-lg!`,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: `focus-within:ring-2 focus-within:ring-(--color-foreground)`,
    },

    multiple: {
      true: `flex-wrap`,
      false: ``,
    },

    block: {
      true: `w-full!`,
      false: `w-auto!`,
    },

    square: {
      true: `aspect-square [flex:0_0_auto]`,
      false: ``,
    },
  },

  compoundVariants: [
    { square: "true", size: "xs", class: "px-0! aspect-square" },
    { square: "true", size: "sm", class: "px-0! aspect-square" },
    { square: "true", size: "md", class: "px-0! aspect-square" },
    { square: "true", size: "lg", class: "px-0! aspect-square" },
    { square: "true", size: "xl", class: "px-0! aspect-square" },
    { multiple: true, size: "xs", class: "h-auto! min-h-6! py-1!" },
    { multiple: true, size: "sm", class: "h-auto! min-h-8! py-1.5!" },
    { multiple: true, size: "md", class: "h-auto! min-h-10! py-1.5!" },
    { multiple: true, size: "lg", class: "h-auto! min-h-12! py-2!" },
    { multiple: true, size: "xl", class: "h-auto! min-h-14! py-2.5!" },
  ],

  defaults: {
    variant: "subtle",
    size: "md",
    disabled: false,
    multiple: false,
    block: false,
    square: false,
  },
});

/**
 * Tailwind-like class variants for the InputTag wrapper (used by Input, Select, Combobox).
 * Owns drag-reorder visual state (`data-dragging`, `data-drag-over`) and matches per-size
 * border radius to the inner e-chip / e-button so the drop ring follows the chip corners.
 */
export const inputTagClasses = defineVariants({
  base: `
    e-input-tag-wrapper
    inline-flex
    transition-opacity
    data-drag-over:ring-2 data-drag-over:ring-(--color-foreground)
    data-dragging:opacity-40
    [[draggable=true]]:cursor-grab
  `,

  variants: {
    size: {
      xs: `rounded`,
      sm: `rounded-md`,
      md: `rounded-lg`,
      lg: `rounded-lg`,
      xl: `rounded-lg`,
    },
  },

  defaults: {
    size: "md",
  },
});

/**
 * Tailwind `part-[base]:` overrides applied to the InputTag remove e-button so its
 * inner part(base) matches the e-chip dimensions per size. The `!` modifiers beat
 * e-button's own size classes since both carry !important.
 *
 * Note: per-size classes use `h-N` matching e-chip h: xs=4, sm=5, md=6, lg=7, xl=8.
 */
export const inputTagRemoveButtonClasses = defineVariants({
  base: `
    part-[base]:p-0!
    part-[base]:min-w-0!
    part-[base]:aspect-square
  `,
  variants: {
    size: {
      xs: `part-[base]:h-4! part-[base]:w-4!`,
      sm: `part-[base]:h-5! part-[base]:w-5!`,
      md: `part-[base]:h-6! part-[base]:w-6!`,
      lg: `part-[base]:h-7! part-[base]:w-7!`,
      xl: `part-[base]:h-8! part-[base]:w-8!`,
    },
  },
  defaults: { size: "md" },
});
